'use client';

import { FC, useMemo } from 'react';

import { User, UserGroups } from '@ravago/shared/authentication';
import { NormalizedUnitOfMeasure } from '@ravago/shared/page-data/models/contentful-mapped';
import { RelatedDocument } from '@ravago/shared/page-data/models/elements';
import { Badge } from '@ravago/shared/radiance/components/badge/components/Badge/Badge';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BoxItem } from '@ravago/shared/radiance/components/box/components/BoxItem/BoxItem';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { IconButton } from '@ravago/shared/radiance/components/button/components/IconButton/IconButton';
import { Checkbox } from '@ravago/shared/radiance/components/checkbox/components/Checkbox/Checkbox';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { DownloadOption } from '@ravago/shared/radiance/components/input-field/components/Download/Download';
import { Tooltip } from '@ravago/shared/radiance/components/tooltip/components/Tooltip/Tooltip';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { useDocumentShare } from '../../../../../hooks/use-document-share';
import { getLanguageNameFromLanguageCode } from '../../../../../utils/locale/locale.utils';
import { Link } from '../../../../shared/link/link';
import { LoginButton } from '../../../../shared/login-button/login-button';
import { ShareAnchor } from '../../../../shared/share-anchor/share-anchor';
import { RelatedDocumentTdsDownload } from '../related-document-tds-download/related-document-tds-download';
import { RelatedDocumentUlProspectorLink } from '../related-document-tds-download/related-document-ul-prospector-link';

const defaultLanguageOption = (locale: string) => ({
   id: 'en',
   value: getLanguageNameFromLanguageCode('en', locale)
});

interface BodyProps {
   locale: string;
   document: RelatedDocument;
   superCondensedView: boolean;
   sharedDocument: boolean;
   condensedView: boolean;
   onLanguageChange?: (lan: DownloadOption | undefined) => void;
   onUOMChange?: (uom: DownloadOption | undefined) => void;
   onDocumentDownload: () => void;
   onViewDocument: () => void;
   availableLanguages: DownloadOption[];
   channelUOMs: Array<NormalizedUnitOfMeasure>;
   detailPageName?: string;
   ulProspectorId: string | undefined;
   onShareChecked: (document: RelatedDocument) => void;
   publicShowTdsUom: boolean;
   isAuthenticated: boolean;
   hasSharePermission?: boolean;
   selectedUOM?: DownloadOption;
   selectedLanguage?: DownloadOption;
   showTdsUom: boolean;
   user?: User;
   language: string;
   canDownloadDocument: boolean;
}

const DocumentListItemBody: FC<BodyProps> = ({
   locale,
   document,
   superCondensedView,
   sharedDocument,
   condensedView,
   ulProspectorId,
   onDocumentDownload,
   onViewDocument,
   onShareChecked,
   onLanguageChange,
   selectedLanguage,
   hasSharePermission,
   selectedUOM,
   onUOMChange,
   isAuthenticated,
   availableLanguages,
   detailPageName,
   language,
   user,
   channelUOMs,
   publicShowTdsUom,
   showTdsUom,
   canDownloadDocument
}) => {
   const translate = useTranslations();
   const formatter = useFormatter();
   const { shareDocumentList } = useDocumentShare();

   const handleLanguageChange = async (lan: DownloadOption | undefined) => {
      if (lan) onLanguageChange?.(lan);
   };
   const handleUOMChange = async (uom: DownloadOption | undefined) => {
      if (uom) onUOMChange?.(uom);
   };

   const handleDownload = async () => {
      onDocumentDownload?.();
   };

   const handleView = async () => {
      onViewDocument?.();
   };

   const isInternal = useMemo(
      () =>
         !!user?.groups?.some((group) =>
            [
               UserGroups.ravagoSalesRepresentative,
               UserGroups.ravagoGlobalGroupAdmin,
               UserGroups.ravagoGlobalViewer,
               UserGroups.ravagoGlobalEditor
            ].includes(group)
         ),
      [user?.groups]
   );

   const loginButton = <LoginButton fullwidth={superCondensedView} buttonLabel={translate('related-documents.login-button')} />;

   const internalUseOnlyBadge = (
      <Tooltip whiteSpace="nowrap" pointers text={translate('related-documents.internal-use-tooltip')}>
         <Badge variant="warning" trailingIcon="info-circle">
            <Typography variant="small" component="p">
               {translate('related-documents.internal-use')}
            </Typography>
         </Badge>
      </Tooltip>
   );

   const fullOrIconButton = superCondensedView ? (
      <Box direction="column" gap="sm" width="100%">
         <Button fullWidth variant="primary" onClick={() => handleDownload()} leadingIcon="download">
            {translate('product.documents.actions.download')}
         </Button>
         <Button fullWidth variant="secondary" onClick={() => handleView()} leadingIcon="eye">
            {translate('product.documents.actions.view')}
         </Button>
      </Box>
   ) : (
      <Box direction="row" gap="xs" items="center">
         <IconButton variant="on-light" backdrop={false} onClick={() => handleDownload()} icon="download" />
         <IconButton variant="on-light" backdrop={false} onClick={() => handleView()} icon="eye" />
      </Box>
   );

   const waitingOrDownload = !document.isAvailable ? (
      <Box direction="row" items="center" gap="xs">
         <Icon icon="clock" color="weak" />
         <Typography variant="caption">{translate('product.documents.waiting-verification')}</Typography>
      </Box>
   ) : (
      fullOrIconButton
   );

   const isDocumentStandardTds = document.isStandardTDS ? (
      <Typography component="p" variant="caption">
         {translate('related-documents.no-tds-available')}
      </Typography>
   ) : (
      waitingOrDownload
   );

   const downloadButtons =
      document.isStandardTDS && !ulProspectorId && isAuthenticated ? (
         !superCondensedView && <Badge variant="disabled">{translate('related-documents.not-available')}</Badge>
      ) : (
         <Box direction="column" gap="xs" items={document.isStandardTDS && superCondensedView && isAuthenticated ? 'center' : 'stretch'}>
            <Box direction="row" gap="sm" justify="end">
               {canDownloadDocument ? isDocumentStandardTds : loginButton}
            </Box>
            {document.isStandardTDS && !!ulProspectorId && isInternal && (
               <RelatedDocumentUlProspectorLink language={language} ulProspectorId={ulProspectorId} centered={superCondensedView} />
            )}
         </Box>
      );

   const tdsDocument = (
      <>
         <BoxItem grow={4} basis="0">
            <Box direction={condensedView ? 'column' : 'row'} width="100%">
               <Box direction="row" width="100%" items="center" justify="between">
                  <Box direction="column" gap="2xs">
                     <Typography variant="caption">{translate('product.documents.sub-category')}</Typography>
                     <Typography variant="small">{document.type}</Typography>
                  </Box>
                  {!superCondensedView && !condensedView && !document.isStandardTDS && document.description !== 'TDS' && (
                     <Box direction="column" gap="2xs">
                        <Typography variant="caption">{translate('product.documents.issue-date')}</Typography>
                        <Typography variant="small">
                           {document.issueDate
                              ? formatter.dateTime(document.issueDate, {
                                   day: '2-digit',
                                   month: 'short',
                                   year: 'numeric'
                                })
                              : '-'}
                        </Typography>
                     </Box>
                  )}
                  {!superCondensedView && (
                     <Box direction="column" gap="xs" items="start">
                        <Box gap="sm" justify="end">
                           {canDownloadDocument ? (
                              <RelatedDocumentTdsDownload
                                 documentId={document.id}
                                 showTdsUom={showTdsUom || (!isAuthenticated && publicShowTdsUom)}
                                 onLanguageChange={(lan) => handleLanguageChange(lan)}
                                 onDownload={() => handleDownload()}
                                 onViewDocument={() => handleView()}
                                 onUOMChange={(uom) => handleUOMChange(uom)}
                                 languages={availableLanguages}
                                 ulProspectorId={ulProspectorId}
                                 selectedLanguage={selectedLanguage ?? defaultLanguageOption(locale)}
                                 selectedUOM={selectedUOM}
                                 channelUOMs={channelUOMs}
                                 isInternal={isInternal}
                                 language={language}
                              />
                           ) : (
                              loginButton
                           )}
                           {!sharedDocument &&
                              !document.isStandardTDS &&
                              !condensedView &&
                              !document.isInternal &&
                              isAuthenticated &&
                              hasSharePermission && <ShareAnchor documentInfo={{ detailPageName, name: document.description, id: document.id }} />}
                        </Box>
                        {ulProspectorId && isInternal && <RelatedDocumentUlProspectorLink language={language} ulProspectorId={ulProspectorId} />}
                     </Box>
                  )}
               </Box>
            </Box>
         </BoxItem>
         {superCondensedView && (
            <BoxItem grow={1}>
               {canDownloadDocument ? (
                  <RelatedDocumentTdsDownload
                     documentId={document.id}
                     ulProspectorId={ulProspectorId}
                     showTdsUom={showTdsUom || (!isAuthenticated && publicShowTdsUom)}
                     onLanguageChange={(lan) => handleLanguageChange(lan)}
                     onDownload={() => handleDownload()}
                     onViewDocument={() => handleView()}
                     onUOMChange={(uom) => handleUOMChange(uom)}
                     languages={availableLanguages}
                     selectedLanguage={selectedLanguage ?? defaultLanguageOption(locale)}
                     isInternal={isInternal}
                     superCondensedView
                     selectedUOM={selectedUOM}
                     channelUOMs={channelUOMs}
                     language={language}
                  />
               ) : (
                  loginButton
               )}
            </BoxItem>
         )}
      </>
   );

   const standardLoggedInTitle = isInternal ? (
      <Link disabled={!ulProspectorId} href={`https://materials.ulprospector.com/en/profile/default?e=${ulProspectorId}`} target="_blank">
         <Typography color="inherit">{document.description}</Typography>
      </Link>
   ) : (
      <Box gap="none" items="center">
         <Typography color="primary">{document.description}</Typography>
      </Box>
   );

   const regularDocument = (
      <BoxItem grow={4} basis="0">
         <Box direction={superCondensedView ? 'column' : 'row'} gap="sm" width="100%">
            <Box
               width="100%"
               gap="sm"
               direction={superCondensedView ? 'column' : 'row'}
               justify={superCondensedView ? 'start' : 'between'}
               items={superCondensedView ? 'stretch' : 'center'}
            >
               <Box direction="column" gap="2xs" width="150px">
                  <Typography variant="caption">{translate('product.documents.sub-category')}</Typography>
                  <Typography variant="small">{document.type}</Typography>
               </Box>
               {!superCondensedView && !condensedView && (
                  <Box direction="column" gap="2xs" width="150px">
                     <Typography variant="caption">{translate('product.documents.issue-date')}</Typography>
                     <Typography variant="small">
                        {document.issueDate
                           ? formatter.dateTime(document.issueDate, {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                             })
                           : '-'}
                     </Typography>
                  </Box>
               )}
               {(!document.isStandardTDS || (document.isStandardTDS && !superCondensedView)) && (
                  <Box width="340px" direction="row" gap="xs" justify={superCondensedView ? 'start' : 'end'}>
                     <Box width="100%" justify={document.isStandardTDS ? 'end' : 'between'} items="center">
                        {!document.isStandardTDS && (
                           <Box direction="column" gap="2xs">
                              <Typography variant="caption">{translate('product.documents.language')}</Typography>
                              <Typography variant="small">{document.language}</Typography>
                           </Box>
                        )}
                        {!superCondensedView && (document.isAvailable || !isAuthenticated) && downloadButtons}
                     </Box>
                     {isAuthenticated && !document.isAvailable && (
                        <Box direction="row" items="center" gap="xs">
                           <Icon icon="clock" color="weak" />
                           <Typography variant="caption">{translate('product.documents.waiting-verification')}</Typography>
                        </Box>
                     )}
                     {!sharedDocument &&
                        !document.isStandardTDS &&
                        !condensedView &&
                        document.isAvailable &&
                        !document.isInternal &&
                        isAuthenticated &&
                        hasSharePermission && <ShareAnchor documentInfo={{ detailPageName, name: document.description, id: document.id }} />}
                  </Box>
               )}
            </Box>
            {superCondensedView && downloadButtons}
         </Box>
      </BoxItem>
   );

   return (
      <Box direction={condensedView ? 'column' : 'row'} gap="sm" items={condensedView ? 'stretch' : 'center'}>
         {document.isStandardTDS && !ulProspectorId && superCondensedView && (
            <Badge variant="disabled">{translate('related-documents.not-available')}</Badge>
         )}
         {document.isInternal && condensedView && internalUseOnlyBadge}
         <BoxItem grow={3} basis={0}>
            <Box gap="none" justify="between" direction="row">
               {canDownloadDocument && document.isAvailable && (
                  <Box gap="xs" items="center">
                     <Box direction="row" gap="2xs" items="center">
                        {!sharedDocument && hasSharePermission && !document.isStandardTDS && (
                           <Checkbox
                              checked={shareDocumentList.documents.map((doc) => doc.id).includes(document.id)}
                              onChange={() => onShareChecked(document)}
                              id={document.id}
                           >
                              {}
                           </Checkbox>
                        )}
                        {document.isStandardTDS ? (
                           standardLoggedInTitle
                        ) : (
                           <Link onClick={() => handleDownload()}>
                              <Typography color="inherit">{document.description}</Typography>
                           </Link>
                        )}
                     </Box>
                     {document.isInternal && !condensedView && internalUseOnlyBadge}
                  </Box>
               )}
               {((canDownloadDocument && !document.isAvailable) || !canDownloadDocument) && (
                  <Box gap="none" items="center">
                     <Typography color="primary">{document.description}</Typography>
                  </Box>
               )}
               {!sharedDocument && !document.isStandardTDS && condensedView && !document.isInternal && isAuthenticated && hasSharePermission && (
                  <ShareAnchor documentInfo={{ detailPageName, name: document.description, id: document.id }} />
               )}
            </Box>
         </BoxItem>
         {document.isBrandedTds ? tdsDocument : regularDocument}
      </Box>
   );
};

export default DocumentListItemBody;
